import React, { useEffect, useState } from "react";
import { useLaneCopy } from "../../utils/laneCopy";
import { useIndonesiaV2Language } from "./indonesiaV2/indonesiaV2Language";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

// ─── Types (mirrors backend services/matescanService.js response) ─────────────

interface TeamAnalysisItem {
  pair: string;
  insight: string;
}

interface MatescanGroupResponse {
  validate: string;
  team_analysis: TeamAnalysisItem[];
  advice: {
    today: string[];
    this_week: string[];
  };
  future_mindset: string;
  ending_note: string;
}

interface ScanData {
  scanId: string;
  matescan_group_response: MatescanGroupResponse;
}

// ─── Bilingual copy (English / Bahasa Indonesia) ──────────────────────────────

const COPY = {
  en: {
    pageTitle: "Matescan Group",
    back: "← Back",
    validateTitle: "Validate & Comfort",
    teamAnalysisTitle: "How Your Team's Energy Connects",
    teamAnalysisSubtitle:
      "Where things flow easily, and where they might need a little more room.",
    teamManagementTitle: "Team Management Plan",
    teamManagementSubtitle:
      "A few things worth trying — no pressure, just options.",
    today: "Today",
    thisWeek: "This Week",
    leadingForwardTitle: "Leading This Team Forward",
    footerNote: "a gentle read on your team's energy — not a verdict on anyone",
    noData: "No scan data found.",
    loadFailed: "Failed to load scan result.",
    runScan: "Run a Scan",
    newScan: "👥 New Matescan Group Scan",
  },
  id: {
    pageTitle: "Matescan Grup",
    back: "← Kembali",
    validateTitle: "Validasi & Kenyamanan",
    teamAnalysisTitle: "Bagaimana Energi Tim Anda Terhubung",
    teamAnalysisSubtitle:
      "Di mana hal-hal mengalir dengan mudah, dan di mana mungkin membutuhkan sedikit lebih banyak ruang.",
    teamManagementTitle: "Rencana Manajemen Tim",
    teamManagementSubtitle:
      "Beberapa hal yang patut dicoba — tanpa tekanan, hanya pilihan.",
    today: "Hari Ini",
    thisWeek: "Minggu Ini",
    leadingForwardTitle: "Memimpin Tim Ini ke Depan",
    footerNote:
      "bacaan lembut tentang energi tim Anda — bukan vonis untuk siapa pun",
    noData: "Tidak ada data pemindaian yang ditemukan.",
    loadFailed: "Gagal memuat hasil pemindaian.",
    runScan: "Jalankan Pemindaian",
    newScan: "👥 Pemindaian Matescan Grup Baru",
  },
} as const;

type Lang = keyof typeof COPY;
type Copy = (typeof COPY)[Lang];

// ─── Animation variants (mirrors CheckMateResult) ──────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.07,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// ─── Shared UI atoms (mirrors CheckMateResult styling) ─────────────────────────

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  accent?: string;
  index?: number;
  children: React.ReactNode;
}> = ({ title, subtitle, accent = "#C084FC", index = 0, children }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    className="bg-[#1a131f] border border-white/10 rounded-3xl p-5 sm:p-6 md:p-8 mb-5 shadow-sm"
  >
    <div className="flex items-center gap-2 mb-1">
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ background: accent }}
      />
      <h3
        className="text-[10px] md:text-xs font-semibold tracking-[0.2em] uppercase"
        style={{ color: accent }}
      >
        {title}
      </h3>
    </div>
    {subtitle && (
      <p
        className="text-[11px] md:text-xs mb-5 ml-3.5"
        style={{ color: "#a89bb5" }}
      >
        {subtitle}
      </p>
    )}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </motion.div>
);

const PairAvatarBadge: React.FC<{ label: string }> = ({ label }) => (
  <div
    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
    style={{ background: "linear-gradient(135deg,#C084FC,#6D4C92)" }}
  >
    {label.slice(0, 1).toUpperCase()}
  </div>
);

const ChecklistGroup: React.FC<{
  label: string;
  items: string[];
  accent: string;
}> = ({ label, items, accent }) => (
  <div>
    <p
      className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide mb-3"
      style={{ color: "#c8bfd1" }}
    >
      {label}
    </p>
    <div className="space-y-2.5">
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-start gap-3 rounded-2xl p-3.5 border"
          style={{ background: "#221a29", borderColor: "rgba(255,255,255,0.1)" }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
            style={{ background: accent }}
          />
          <span
            className="text-xs md:text-sm leading-relaxed"
            style={{ color: "#f7f3fa" }}
          >
            {item}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const MatescanResult: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const lang: Lang = useIndonesiaV2Language();
  const t = useLaneCopy("msresult", COPY);

  const [data, setData] = useState<ScanData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("matescanScan");
    if (!raw) {
      setError(t.noData);
      return;
    }
    try {
      setData(JSON.parse(raw));
    } catch {
      setError(t.loadFailed);
    }
  }, [t]);

  const handleNewScan = () => {
    sessionStorage.removeItem("matescanScan");
    navigate("/matescan");
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#221a29" }}
      >
        <div className="text-center">
          <p className="text-[#ff8a9b] text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate("/matescan")}
            className="px-6 py-2.5 rounded-[14px] bg-[#a78bff] text-[#0f0a14] text-xs font-semibold"
          >
            {t.runScan}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0f0a14] [color-scheme:dark] flex items-center justify-center">
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: "#C084FC33", borderTopColor: "#C084FC" }}
        />
      </div>
    );
  }

  const { validate, team_analysis, advice, future_mindset, ending_note } =
    data.matescan_group_response;

  return (
    <div className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark]">
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Header nav */}
          <div className="mb-8 relative flex items-center justify-center min-h-[40px]">
            <div className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
              <span
                className="text-[10px] md:text-xs tracking-[0.2em] uppercase font-medium"
                style={{ color: "#a89bb5" }}
              >
                {t.pageTitle}
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
            </div>

            <button
              onClick={handleNewScan}
              className="absolute right-0 inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-white/10 bg-[#1a131f] px-4 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.15em] text-[#c8bfd1] shadow-sm hover:bg-[#221a29] transition flex-shrink-0"
            >
              {t.back}
            </button>
          </div>

          <div className="space-y-5">
            {/* ── PART 1: VALIDATE & COMFORT ── */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="rounded-3xl p-6 sm:p-8 text-center shadow-sm border"
              style={{ background: "#221a29", borderColor: "rgba(255,255,255,0.1)" }}
            >
              <div className="text-3xl sm:text-4xl mb-4">🕊</div>
              <p
                className="text-sm sm:text-base leading-relaxed max-w-md mx-auto"
                style={{ color: "#f7f3fa" }}
              >
                {validate}
              </p>
            </motion.div>

            {/* ── PART 2: ENERGY MATCH ANALYSIS ── */}
            <SectionCard
              title={t.teamAnalysisTitle}
              subtitle={t.teamAnalysisSubtitle}
              accent="#C084FC"
              index={1}
            >
              <div className="space-y-3">
                {team_analysis.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-2xl p-4 border"
                    style={{ background: "#221a29", borderColor: "rgba(255,255,255,0.1)" }}
                  >
                    <PairAvatarBadge label={item.pair} />
                    <div>
                      <p
                        className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide mb-1"
                        style={{ color: "#c8bfd1" }}
                      >
                        {item.pair}
                      </p>
                      <p
                        className="text-xs md:text-sm leading-relaxed"
                        style={{ color: "#f7f3fa" }}
                      >
                        {item.insight}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── PART 3: ACTIONABLE ADVICE ── */}
            <SectionCard
              title={t.teamManagementTitle}
              subtitle={t.teamManagementSubtitle}
              accent="#d9ccff"
              index={2}
            >
              <div className="space-y-6">
                <ChecklistGroup
                  label={t.today}
                  items={advice.today}
                  accent="#C084FC"
                />
                <ChecklistGroup
                  label={t.thisWeek}
                  items={advice.this_week}
                  accent="#7DD3FC"
                />
              </div>
            </SectionCard>

            {/* ── PART 4: FUTURE & MINDSET ── */}
            <SectionCard
              title={t.leadingForwardTitle}
              accent="#C084FC"
              index={3}
            >
              <p
                className="text-xs md:text-sm leading-relaxed"
                style={{ color: "#f7f3fa" }}
              >
                {future_mindset}
              </p>
            </SectionCard>

            {/* ── PART 5: ENDING NOTE ── */}
            <motion.div
              custom={4}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="rounded-3xl p-6 sm:p-8 text-center shadow-sm border"
              style={{ background: "rgba(0,168,120,0.12)", borderColor: "rgba(0,168,120,0.4)" }}
            >
              <p
                className="text-sm sm:text-base font-medium leading-relaxed"
                style={{ color: "#7ee2c0" }}
              >
                {ending_note}
              </p>
            </motion.div>
          </div>

          {/* Footer actions */}
          <div className="flex justify-center mt-10 pb-4">
            <button
              onClick={handleNewScan}
              className="w-full sm:w-auto py-3.5 px-12 rounded-[14px] bg-[#a78bff] text-xs font-semibold uppercase tracking-[0.15em] text-[#0f0a14] shadow-[0_0_22px_rgba(167,139,255,0.35)] transition-all duration-200 hover:opacity-90 transform hover:-translate-y-0.5"
            >
              {t.newScan}
            </button>
          </div>

          <div
            className="text-center mt-3 pb-8 text-[10px] md:text-[11px] tracking-wide"
            style={{ color: "#a89bb5" }}
          >
            <span
              className="inline-block border-t w-10 sm:w-16 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
            {t.footerNote}
            <span
              className="inline-block border-t w-10 sm:w-16 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatescanResult;
